import React, { useState, useEffect } from "react";
import Modal from "newui/components/ui/Modal/Modal";

interface DeclineReasonModalProps {
	isOpen: boolean;
	count: number;
	itemLabel?: string;
	reasons: Record<string, string>;
	defaultReason?: string;
	onClose: () => void;
	onConfirm: (reason: string, category: string) => void;
	isLoading: boolean;
}

export const DeclineReasonModal: React.FC<DeclineReasonModalProps> = ({
	isOpen,
	count,
	itemLabel = "item",
	reasons,
	defaultReason,
	onClose,
	onConfirm,
	isLoading,
}) => {
	const reasonKeys = Object.keys(reasons);
	const initialCategory = defaultReason ?? reasonKeys[0] ?? "";
	const [category, setCategory] = useState(initialCategory);
	const [otherReason, setOtherReason] = useState("");

	useEffect(() => {
		if (isOpen) {
			setCategory(initialCategory);
			setOtherReason("");
		}
	}, [isOpen, initialCategory]);

	const isValid = category !== "other" || otherReason.trim().length > 0;

	const handleConfirm = () => {
		if (!isValid) {
			return;
		}
		const reason = category === "other"
			? otherReason.trim()
			: reasons[category];
		onConfirm(reason, category);
	};

	const pluralLabel = count === 1 ? itemLabel : `${itemLabel}s`;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Decline ${count} ${pluralLabel}`}
			size="md"
			modalType="standard"
			iconType="warning"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: isLoading ? "Declining..." : `Decline ${count} ${pluralLabel}`,
				onClick: handleConfirm,
				btntype: "primary",
				disabled: isLoading || !isValid,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="space-y-3">
				<p className="text-sm text-gray-600">
					Select a reason for declining {count > 1 ? `these ${count} ${pluralLabel}` : `this ${itemLabel}`}:
				</p>
				<div className="space-y-2">
					{reasonKeys.map((key) => (
						<label
							key={key}
							className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
							data-testid={`decline-option-${key}`}
						>
							<input
								type="radio"
								name="decline-reason"
								value={key}
								checked={category === key}
								onChange={() => setCategory(key)}
								className="mt-0.5 text-blue-600"
							/>
							<span>{reasons[key]}</span>
						</label>
					))}
				</div>
				{category === "other" && (
					<div>
						<label htmlFor="decline-other-reason" className="mb-1 block text-xs font-medium text-gray-500">
							Specify reason:
						</label>
						<textarea
							id="decline-other-reason"
							value={otherReason}
							onChange={(e) => setOtherReason(e.target.value)}
							placeholder="Enter the reason for declining..."
							rows={3}
							maxLength={500}
							className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-300"
							data-testid="decline-other-reason-input"
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};
